import { useCallback, useEffect, useRef } from "react";
import { driver } from "driver.js";
import "driver.js/dist/driver.css";
import "@/styles/tour.css";

/**
 * useTour — wraps driver.js with LicitaIA defaults.
 *
 * @param {string} tourId    Stable identifier (e.g. "tour:crm") used for
 *                           localStorage persistence so the tour doesn't
 *                           pop up again after the user skips/finishes.
 * @param {Array}  steps     driver.js step config — every step must have
 *                           `element` (CSS selector or DOM node) and a
 *                           `popover: { title, description }`.
 * @param {Object} opts      { autoStart?: boolean }
 *                           autoStart=true triggers the tour the first
 *                           time the user lands on the page (unless they
 *                           already saw/skipped it).
 *
 * Returns { start, stop, hasSeen, markSeen, resetSeen }.
 */
export function useTour(tourId, steps, { autoStart = false } = {}) {
    const driverRef = useRef(null);

    const hasSeen = useCallback(() => {
        try {
            return localStorage.getItem(tourId) === "done";
        } catch {
            return false;
        }
    }, [tourId]);

    const markSeen = useCallback(() => {
        try {
            localStorage.setItem(tourId, "done");
        } catch {
            // ignore (private mode etc.)
        }
    }, [tourId]);

    const resetSeen = useCallback(() => {
        try {
            localStorage.removeItem(tourId);
        } catch {
            // ignore
        }
    }, [tourId]);

    const start = useCallback(() => {
        if (driverRef.current) {
            try {
                driverRef.current.destroy();
            } catch {
                // already destroyed
            }
        }
        const validSteps = (steps || []).filter((s) => {
            if (!s?.element) return true; // text-only steps OK
            if (typeof s.element === "string") {
                return !!document.querySelector(s.element);
            }
            return true;
        });
        if (validSteps.length === 0) return;
        const d = driver({
            showProgress: true,
            allowClose: true,
            smoothScroll: true,
            overlayColor: "rgba(15, 23, 42, 0.65)",
            popoverClass: "licitaia-tour",
            stagePadding: 6,
            stageRadius: 14,
            nextBtnText: "Siguiente →",
            prevBtnText: "← Atrás",
            doneBtnText: "Entendido",
            progressText: "Paso {{current}} de {{total}}",
            onDestroyStarted: () => {
                markSeen();
                if (d) {
                    try {
                        d.destroy();
                    } catch {
                        // already destroyed
                    }
                }
            },
            steps: validSteps,
        });
        driverRef.current = d;
        d.drive();
    }, [steps, markSeen]);

    const stop = useCallback(() => {
        if (driverRef.current) {
            try {
                driverRef.current.destroy();
            } catch {
                // already destroyed
            }
            driverRef.current = null;
        }
    }, []);

    // Auto-start once per identifier.
    useEffect(() => {
        if (!autoStart) return;
        if (hasSeen()) return;
        // Suppress the per-page tour while the global welcome tour is
        // running, otherwise the popovers stack on top of each other.
        try {
            if (
                localStorage.getItem("tour:welcome:step") != null &&
                localStorage.getItem("tour:welcome") !== "done"
            ) {
                return;
            }
        } catch {
            // ignore
        }
        // Defer a bit so the page (and selectors) actually exist.
        const t = setTimeout(() => start(), 900);
        return () => {
            clearTimeout(t);
            stop();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [autoStart]);

    return { start, stop, hasSeen, markSeen, resetSeen };
}
